import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/useSessionHook";
import supabase from "@/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Age gate (self-attestation MVP).
 * Stores `age_verified=true` and `age_verified_at=now()` on the user's profile.
 */
export default function AgeGatePage() {
  const navigate = useNavigate();
  const { session, profile, refreshProfile } = useSession();

  const [checked, setChecked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onContinue = async () => {
    setError(null);

    if (!session?.user?.id) {
      setError("You must be logged in.");
      return;
    }

    if (!checked) {
      setError("You must confirm you are 18+ to continue.");
      return;
    }

    setIsSaving(true);
    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          age_verified: true,
          age_verified_at: new Date().toISOString(),
          age_verification_method: "self_attestation",
        })
        .eq("id", session.user.id);

      if (updateError) throw updateError;

      await refreshProfile();
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save age verification.");
    } finally {
      setIsSaving(false);
    }
  };

  const displayName = (profile as any)?.display_name || (profile as any)?.username || "";

  return (
    <div className="jr-app-shell items-center justify-center p-4">
      <Card className="jr-card w-full max-w-lg border-[color:var(--jr-border)]">
        <CardHeader className="space-y-2">
          <div className="jr-kicker">Private Access</div>
          <CardTitle className="jr-display text-4xl font-semibold">Age Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm leading-6 text-[var(--jr-text-soft)]">
            This app contains adult content. You must confirm you are at least 18 years old to continue.
          </p>

          {displayName ? (
            <p className="text-sm text-[var(--jr-text-soft)]">Signed in as <span className="font-semibold text-[var(--jr-text)]">{displayName}</span></p>
          ) : null}

          <div className="jr-card-muted flex items-start gap-3 p-4">
            <Checkbox checked={checked} onCheckedChange={(v) => setChecked(v === true)} className="mt-0.5 border-[color:var(--jr-border-strong)] data-[state=checked]:border-[color:var(--jr-accent-strong)] data-[state=checked]:bg-[color:var(--jr-accent-strong)] data-[state=checked]:text-[#241514]" />
            <div className="text-sm">
              <div className="font-medium text-[var(--jr-text)]">I confirm I am 18 years of age or older.</div>
              <div className="text-[var(--jr-text-muted)]">(Self-attestation — no ID is collected.)</div>
            </div>
          </div>

          {error ? <div className="jr-status-error">{error}</div> : null}

          <Button onClick={onContinue} disabled={isSaving} className="jr-action-primary h-auto w-full min-h-12">
            {isSaving ? "Saving…" : "Continue"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
